/**
 * The bottom tab bar on a phone - shared by all three shells.
 *
 * WHY ROLE-WISE (13-10-2026)
 *
 * Until today the Materials bar was the same four buttons for everybody -
 * Home, PRs, GRN, Stock - and Service and Finance had no bar at all. A CMD
 * got a GRN button he never uses and no way to reach Purchase Orders or
 * Liabilities without opening the menu; Accounts got two buttons for screens
 * it cannot even open. Each shell now hands this component its own list of
 * the screens each role uses most, and the bar shows the first five that the
 * role is actually allowed into.
 *
 * WHAT IT NEVER DOES
 *
 * It never offers a screen the role cannot open: every button passes through
 * the same canSee rule the sidebar uses. If a role's own list comes up short,
 * it is topped up from the module's fallback list - again only with screens
 * the role may open - so the bar is never a row of dead buttons. Everything
 * else stays one tap away in the menu at the top left, exactly as before.
 *
 * Hidden on a laptop (lg and up), where the sidebar is always open.
 */
import { NavLink } from 'react-router-dom'
import clsx from 'clsx'
import type { LucideIcon } from 'lucide-react'

export interface MobileTab {
  to: string
  label: string
  icon: LucideIcon
  /** Exact match only - for a module's home ('/', '/service', '/finance'). */
  end?: boolean
}

/** At most five: on a 360 px phone each button keeps about 72 px. */
export const MAX_MOBILE_TABS = 5

/**
 * The buttons for one role: its own list first, then the fallback list, keeping
 * only routes the role may open, without duplicates, at most five.
 */
export function pickMobileTabs(
  wanted: string[],
  fallback: string[],
  catalogue: Record<string, MobileTab>,
  canSee: (to: string) => boolean,
): MobileTab[] {
  const out: MobileTab[] = []
  for (const to of [...wanted, ...fallback]) {
    if (out.length >= MAX_MOBILE_TABS) break
    const tab = catalogue[to]
    if (!tab || out.some((t) => t.to === to) || !canSee(to)) continue
    out.push(tab)
  }
  return out
}

/** Room the fixed bar takes, so the end of every page can scroll clear of it. */
export function MobileTabSpacer() {
  return (
    <div aria-hidden className="no-print h-16 shrink-0 lg:hidden"
         style={{ marginBottom: 'env(safe-area-inset-bottom)' }} />
  )
}

export function MobileTabBar({ tabs }: { tabs: MobileTab[] }) {
  if (tabs.length === 0) return null
  return (
    <nav
      className="no-print fixed inset-x-0 bottom-0 z-20 grid border-t border-slate-200 bg-white lg:hidden"
      style={{
        gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))`,
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {tabs.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          className={({ isActive }) => clsx(
            'flex min-w-0 flex-col items-center gap-0.5 px-0.5 py-2 text-[11px] font-medium',
            isActive ? 'text-oval-700' : 'text-slate-500',
          )}
        >
          <t.icon className="h-5 w-5 shrink-0" />
          <span className="w-full truncate text-center">{t.label}</span>
        </NavLink>
      ))}
    </nav>
  )
}
